// Image modal — singleton lightbox shared across all galleries.
//
// Binding model: we attach click + pointerdown DIRECTLY to each
// [data-modal-item] element (not via document delegation). Element-level
// listeners fire in the TARGET phase, which runs BEFORE the click bubbles
// up to the Swiper container — that's the only reliable way to receive
// clicks inside a Swiper carousel, because Swiper's own click handler
// calls preventDefault / stopPropagation when it considers the gesture a
// drag.
//
// The trigger's nearest [data-image-modal-source] ancestor defines the
// slide set, which keeps multiple galleries on the same page independent
// (clicking a slide in gallery A opens the modal showing only A's slides,
// starting at the clicked index).
//
// Each trigger element advertises its own data:
//   data-modal-index    — slide index (number)
//   data-modal-src      — full-size image URL
//   data-modal-caption  — caption text (from asset.caption in CMS)
//   data-modal-alt      — alt text
//
// Close on: X click, scrim click, Escape key. Lenis is paused while open
// so the page behind doesn't drift.

import Swiper, { Navigation, Keyboard } from 'imports/swiper'

const SELECTOR_MODAL = '[data-image-modal]'
const SELECTOR_TRIGGER = '[data-modal-item]'
const SELECTOR_SOURCE = '[data-image-modal-source]'

// Pointer-distance threshold (px) between pointerdown and click. Beyond
// this we treat the gesture as a drag and suppress the modal open — this
// is a safety net on top of Swiper's own preventClicksPropagation so a
// freeMode drag-release never accidentally launches the lightbox.
const DRAG_THRESHOLD = 8

const imageModal = {
    init() {
        const modal = document.querySelector(SELECTOR_MODAL)
        if (!modal) return

        this.modal = modal
        this.swiperEl = modal.querySelector('[data-image-modal-swiper]')
        this.wrapper = modal.querySelector('.swiper-wrapper')
        this.prevBtn = modal.querySelector('.image-modal__prev')
        this.nextBtn = modal.querySelector('.image-modal__next')
        this.swiper = null
        this.items = []

        // Shared pointer-down tracker (capture phase, document level).
        // Used by every per-element click handler to compute drag distance
        // from the most recent pointerdown — wherever it happened. Without
        // this, if the user pointerdown-ed on slide A and then click fires
        // on slide B (e.g. across a Swiper loop-rewind), B's per-element
        // closure would have stale 0,0 coords and the click would be
        // mistaken for a multi-thousand-pixel drag and silently rejected.
        this._down = { x: 0, y: 0, at: 0 }
        document.addEventListener('pointerdown', (e) => {
            this._down.x = e.clientX
            this._down.y = e.clientY
            this._down.at = Date.now()
        }, true)

        // Bind click handlers DIRECTLY on each trigger element. Target-
        // phase listeners fire BEFORE the event bubbles to the swiper
        // container where Swiper's own click listener may call
        // stopPropagation/preventDefault.
        this._bindAllTriggers()
        setTimeout(() => this._bindAllTriggers(), 0)
        setTimeout(() => this._bindAllTriggers(), 500)
        window.addEventListener('load', () => this._bindAllTriggers())

        // Swiper (loop mode, slideToClickedSlide) can move, remove, or
        // re-insert slide elements at runtime. Watch each source for any
        // DOM changes and rebind anything new so clicks keep working
        // through swipes, programmatic slideTo, etc.
        document.querySelectorAll(SELECTOR_SOURCE).forEach((source) => {
            const obs = new MutationObserver(() => this._bindAllTriggers())
            obs.observe(source, { childList: true, subtree: true })
        })

        // Close: X button + scrim both carry [data-image-modal-close].
        modal.querySelectorAll('[data-image-modal-close]').forEach((el) => {
            el.addEventListener('click', () => this.close())
        })

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !modal.hidden) this.close()
        })
    },

    open(items, startIndex = 0) {
        if (!items || !items.length) return
        this.items = items

        // Build slides for this open. Each slide wraps its image AND its
        // caption in a <figure> with `width: fit-content`, so the caption
        // shares the image's rendered width and naturally left-aligns to
        // the image's left edge instead of the screen's.
        this.wrapper.innerHTML = ''
        items.forEach((item) => {
            const slide = document.createElement('div')
            slide.className = 'swiper-slide image-modal__slide'

            const figure = document.createElement('figure')
            figure.className = 'image-modal__figure'

            const img = document.createElement('img')
            img.src = item.src
            img.alt = item.alt || ''
            figure.appendChild(img)

            if (item.caption) {
                const cap = document.createElement('figcaption')
                cap.className = 'image-modal__slide-caption'
                cap.textContent = item.caption
                figure.appendChild(cap)
            }

            slide.appendChild(figure)
            this.wrapper.appendChild(slide)
        })

        // Single-item galleries don't need arrows.
        const showArrows = items.length > 1
        this.prevBtn.hidden = !showArrows
        this.nextBtn.hidden = !showArrows

        // Reveal + lock the page behind.
        this.modal.hidden = false
        document.body.classList.add('is-image-modal-open')
        if (window.__lenis && typeof window.__lenis.stop === 'function') {
            window.__lenis.stop()
        }

        // Fresh Swiper each open — keeps state predictable across
        // different galleries with different slide counts.
        if (this.swiper) {
            this.swiper.destroy(true, true)
            this.swiper = null
        }

        const safeIndex = Math.min(Math.max(startIndex, 0), items.length - 1)
        const looped = items.length > 1

        this.swiper = new Swiper(this.swiperEl, {
            modules: [Navigation, Keyboard],
            slidesPerView: 1,
            spaceBetween: 32,
            initialSlide: safeIndex,
            loop: looped,
            keyboard: { enabled: true },
            navigation: looped ? {
                prevEl: this.prevBtn,
                nextEl: this.nextBtn,
            } : false,
        })
    },

    close() {
        if (this.modal.hidden) return
        this.modal.hidden = true
        document.body.classList.remove('is-image-modal-open')
        if (window.__lenis && typeof window.__lenis.start === 'function') {
            window.__lenis.start()
        }
        if (this.swiper) {
            this.swiper.destroy(true, true)
            this.swiper = null
        }
        this.wrapper.innerHTML = ''
        this.items = []
    },

    // Scan the DOM for every trigger that hasn't been bound yet and wire
    // each one's click + pointerdown. Idempotent — re-runs are safe.
    _bindAllTriggers() {
        document.querySelectorAll(SELECTOR_TRIGGER).forEach((el) => {
            this._bindTrigger(el)
        })
    },

    _bindTrigger(el) {
        if (el._imageModalBound) return
        el._imageModalBound = true

        el.addEventListener('click', (e) => {
            // Drag check uses the SHARED pointerdown tracker (init), so
            // it stays correct even when the pointerdown happened on a
            // different slide than the one click fires on. Swiper's own
            // drag still works because this listener runs in the TARGET
            // phase, before the click bubbles to the swiper container.
            const dx = Math.abs(e.clientX - this._down.x)
            const dy = Math.abs(e.clientY - this._down.y)
            if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) return
            if (this._down.at && Date.now() - this._down.at > 800) return

            const source = el.closest(SELECTOR_SOURCE)
            if (!source) return

            // Loop-mode Swiper clones slides, so querying the DOM returns each
            // photo more than once and in a shifted order. Build the canonical
            // set keyed by image id (clones share their original's id), ordered
            // by data-modal-index. This makes the modal's slide set identical no
            // matter which clone was clicked.
            const triggers = Array.from(source.querySelectorAll(SELECTOR_TRIGGER))
            const byId = new Map()
            triggers.forEach((t) => {
                if (!t.dataset.modalSrc) return
                const id = t.dataset.modalId || t.dataset.modalSrc
                if (byId.has(id)) return
                byId.set(id, {
                    id,
                    index: parseInt(t.dataset.modalIndex, 10),
                    src: t.dataset.modalSrc,
                    caption: t.dataset.modalCaption || '',
                    alt: t.dataset.modalAlt || '',
                })
            })
            const items = Array.from(byId.values()).sort((a, b) => a.index - b.index)
            if (!items.length) return

            // Start at the photo that was actually clicked, matched by id (not
            // array position, which the clones would shift).
            const clickedId = el.dataset.modalId || el.dataset.modalSrc
            let startIndex = items.findIndex((it) => it.id === clickedId)
            if (startIndex < 0) startIndex = 0

            // Open the modal. We intentionally do NOT stopPropagation —
            // Swiper still gets the click and reconciles its internal
            // loop-mode clone state. The modal opens on top so any
            // background `slideToClickedSlide` movement is invisible.
            this.open(items, startIndex)
        })

        // Keyboard activation — slides have role="button" tabindex="0".
        el.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            el.click()
        })
    },
}

export default imageModal
